import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { JSONRPCError, Permission, PermissionSubject, PermissionVerb } from '@s8l/client-tree-lib';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import Swal from 'sweetalert2';

import { UserModalComponent, UserModalConfig, UserModalUpdate } from 'src/app/components/user-modal/user-modal.component';
import { UserMultiModalComponent, UserMultiModalResult } from 'src/app/components/user-multi-modal/user-multi-modal.component';
import { CmsService } from 'src/app/services/cms.service';
import { CsvService } from 'src/app/services/csv.service';
import { Licence, Profile, Role } from 'src/app/services/jsonrpc/user-server.service';
import { UserService } from 'src/app/services/user.service';

@Component({
  selector: 'app-registered-users',
  templateUrl: './registered-users.component.html',
  styleUrls: ['./registered-users.component.scss']
})
export class RegisteredUsersComponent implements OnInit, OnDestroy {
  public entries: Profile[] = [];
  public clearSelection = new Subject<void>();
  public clearSelectionChange: Observable<void>;

  public get canEdit(): boolean {
    return this.cms.hasAccess(new Permission(PermissionSubject.User, PermissionVerb.Write));
  }

  public get canDelete(): boolean {
    return this.cms.hasAccess(new Permission(PermissionSubject.User, PermissionVerb.Delete));
  }

  public get roles(): BehaviorSubject<Role[]> {
    return this.users.roles;
  }

  private subscription: Subscription;

  constructor(
    private modal: NgbModal,
    private toastr: ToastrService,
    private csv: CsvService,
    private users: UserService,
    private cms: CmsService
  ) {}

  public ngOnInit() {
    this.clearSelectionChange = this.clearSelection.asObservable();
    this.subscription = this.users.users.subscribe(users => (this.entries = users));
  }

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  public copy(item) {
    return JSON.parse(JSON.stringify(item));
  }

  public openCreateModal() {
    const modalRef = this.modal.open(UserModalComponent, { size: 'lg' });
    this.putRolesAndCleanUp(modalRef);
    modalRef.componentInstance.config = this.modalConfig(false);
    modalRef.componentInstance.onSubmit.subscribe((data: UserModalUpdate) => this.create(data.model.profile, data.password));
  }

  public openUpdateModal(profile: Profile) {
    const modalRef = this.modal.open(UserModalComponent, { size: 'lg' });
    this.putRolesAndCleanUp(modalRef);
    modalRef.componentInstance.model = this.copy({ profile });
    modalRef.componentInstance.config = this.modalConfig(true);
    modalRef.componentInstance.onSubmit.subscribe((data: UserModalUpdate) =>
      this.update(data.model.profile, data.password, data.activateLicences, profile.username !== data.model.profile.username)
    );
  }

  public openUpdateMultiModal(profiles: Profile[]) {
    const modalRef = this.modal.open(UserMultiModalComponent, { size: 'lg' });
    this.putRolesAndCleanUp(modalRef, () => this.clearSelection.next());
    modalRef.componentInstance.config = {
      title: `Update ${profiles.length} Users`,
      state: true,
      duration: false
    };
    modalRef.componentInstance.onSubmit.subscribe((res: UserMultiModalResult) => {
      return Promise.all(
        profiles.map(u => {
          if (res.toggleEnabled) u.enabled = res.enabled;
          if (res.toggleRoles) u.meta.roles = res.roles;
          if (res.toggleExpiration) u.meta.expiration = res.expiration;
          return this.update(u, null, []);
        })
      );
    });
  }

  // Pipe information about all available roles into the modal.
  // Optionally perform the cleanup command IF the modal was closed successfully.
  private putRolesAndCleanUp(modalRef: NgbModalRef, cleanup: () => void = null) {
    const rolesSub = this.roles.subscribe(r => (modalRef.componentInstance.roles = r));
    modalRef.result.then(
      () => {
        rolesSub?.unsubscribe();
        if (cleanup) cleanup();
      },
      () => rolesSub?.unsubscribe()
    );
  }

  private modalConfig(update: boolean): UserModalConfig {
    return { header: 'User', update, state: false, settings: true, template: false, pattern: false };
  }

  public reload(): Promise<any> {
    this.entries = [];
    return this.users.loadUsers();
  }

  public create(user: Profile, password?: string): Promise<any> {
    return this.users
      .createUser(user, password)
      .then(() => this.toastr.success(`User "${user.username}" created`))
      .catch(err => this.handleError(err));
  }

  public update(user: Profile, password: string, updateLicences: string[], updateUsername: boolean | null = null): Promise<any> {
    return this.users
      .updateUser(user, password, updateUsername)
      .then(() => Promise.all(updateLicences.map(licence => this.users.activateLicence(user, licence))))
      .then(() => this.toastr.success(`User "${user.username}" updated`))
      .catch(err => this.handleError(err));
  }

  private handleError(err): Promise<any> {
    if (err instanceof JSONRPCError) {
      switch (err.code) {
        case 3011: // already_exists
        case 3034: // profile_registered
          return Swal.fire({
            title: 'Already exists',
            text: 'Update the existing entry instead or choose another configuration',
            icon: 'error'
          });
        case 3121:
          return Swal.fire({
            title: 'Invalid Password',
            text: 'The password is not allowed: ' + (err.data?.info ?? err.message),
            icon: 'error'
          });
      }
    }
    throw err;
  }

  public delete(user: Profile): Promise<any> {
    return this.users.relatedLicencesForDeletion(user.username).then(licences => {
      if (licences != null) {
        return this.users
          .deleteUser(user.uuid)
          .then(() => this.toastr.success(`User "${user.username}" deleted`))
          .then(() => Promise.all(licences.map(l => this.deleteLicence(l))));
      }
    });
  }

  private deleteLicence(template: Licence): Promise<any> {
    return this.users.deleteTemplate(template).then(() => this.toastr.success(`Licence "${template.key}" deleted`));
  }

  public deleteMulti(users: Profile[]): Promise<any> {
    return Promise.all(users.map(e => this.delete(e)));
  }

  public exportToCsv(): void {
    this.csv.export(
      'users-export.csv',
      this.entries.concat.apply(
        [],
        this.entries.map(item => item.meta)
      )
    );
  }
}
